// Import required dependencies
import React, { useState, useEffect } from "react";
import Drawer from "react-modern-drawer";

// Import components
import ButtonIcon from "../../../../../components/ButtonIcon/ButtonIcon";
import StatusDot from "../../../../../components/StatusDot/StatusDot";
import Checkbox from '../../../../../components/Checkbox/Index';
import TagInput from '../../../../../components/form/tag-input';
import Select from '../../../../../components/form/select';
import Radio from '../../../../../components/form/radio-input'

// Import styles
import style from "./FilterDrawer.module.scss";
import "react-modern-drawer/dist/index.css";

// Import constants
import { progression_statuses, sales_left_progression_statuses } from "../../../../../../constants";

// Import icons
import CalendarIcon from "../../../../../assets/svg/icons/common/Calender";
import ArrowDown from "../../../../../assets/svg/icons/common/ArrowDown";

// Import application store
import useApplicationStore from "../../../../../stores/application";
import useCommonStore from "../../../../../stores/common";
import useUserStore from "../../../../../stores/user";

// Import API functions
import { getEmsRepCodes, GetAllOrganizationsSingleCall, GetOrganizationById, getBanks } from "../../../../../../api";


/**
 * FilterDrawer component for filtering applications
 * @component
 * @returns {JSX.Element} FilterDrawer component
 */
function FilterDrawer() {
  const filterDrawerOpen = useCommonStore((state) => state.filterDrawerOpen);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const setAppFilterSearchQuery = useApplicationStore(
    (state) => state.setAppFilterSearchQuery,
  );

  const appFilterSearchQuery = useApplicationStore(
    (state) => state.appFilterSearchQuery,
  );

  const relationshipManagers = useApplicationStore(
    (state) => state.relationshipManagers,
  );
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const user = useUserStore((state) => state.user);

  const [progressions, setProgressions] = useState([]);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [errors, setErrors] = useState({ createdFrom: "", createdTo: "" });
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [showAllSalesStatuses, setShowAllSalesStatuses] = useState(false);
  const [showAllRepCodes, setShowAllRepCodes] = useState(false);
  const [repCodes, setRepCodes] = useState([]);
  const [selectedRepCodes, setSelectedRepCodes] = useState([]);
  const [isLoadingRepCodes, setIsLoadingRepCodes] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState(appFilterSearchQuery.organization_id);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(appFilterSearchQuery.user_id || "");
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(appFilterSearchQuery.bank || null);
  const [progress, setProgress] = useState(appFilterSearchQuery.left_progression_filter || null);
  const [selectedManagers, setSelectedManagers] = useState(appFilterSearchQuery.manager_id);
  const [selectedUnderwriters, setSelectedUnderwriters] = useState(appFilterSearchQuery.uw_ids);
  const [selectedChannel, setSelectedChannel] = useState(appFilterSearchQuery.channel || null);


  useEffect(() => {
    if (filterDrawerOpen) {
      fetchRepCodes();
      fetchOrganizations();
      fetchBanks();
    }
  }, [filterDrawerOpen]);

  useEffect(() => {
    if (user?.channel == "sales_channel" && user?.role == "admin") {
      if(user?.organization_id) {
        fetchOrganizationUsers(user?.organization_id);
      }
    }
  }, [user]);

  /**
   * Fetches representative codes from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchRepCodes = async () => {
    try {
      setIsLoadingRepCodes(true);
      const response = await getEmsRepCodes();

      if (Array.isArray(response)) {
        setRepCodes(response);
      } else if (response.data && Array.isArray(response.data)) {
        setRepCodes(response.data);
      } else {
        setRepCodes([]);
      }
    } catch (error) {
      console.error('Failed to fetch rep codes:', error);
      setRepCodes([]);
    } finally {
      setIsLoadingRepCodes(false);
    }
  };

  /**
   * Fetches banks from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchBanks = async () => {
    try {
      const response = await getBanks();

      if (response.status && response.data) {
        const bankOptions = response.data.map(bank => ({
          label: bank.name,
          value: bank.id
        }));
        // Remove the first bank (usually default/placeholder)
        bankOptions.shift();
        setBanks(bankOptions);
      } else {
        setBanks([]);
      }
    } catch (error) {
      console.error('Failed to fetch banks:', error);
      setBanks([]);
    }
  };

  /**
   * Fetches organizations from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchOrganizations = async () => {
    try {
      const response = await GetAllOrganizationsSingleCall();
      if (response.organization && response.organization.length > 0) {
        const orgOptions = response.organization.map(org => ({
          label: org.name,
          value: org.id
        }));
        setOrganizations(orgOptions);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      setOrganizations([]);
    }
  };

  /**
   * Fetches users for a specific organization
   * @async
   * @param {string} orgId - The organization ID
   * @returns {Promise<void>}
   */
  const fetchOrganizationUsers = async (orgId) => {
    if (!orgId) {
      setOrganizationUsers([]);
      setSelectedUser("");
      return;
    }

    try {
      const response = await GetOrganizationById(orgId);

      if (response.success && response.users && response.users.length > 0) {
        const userOptions = response.users.map(user => ({
          label: user.name,
          value: user.id
        }));
        setOrganizationUsers(userOptions);
      } else {
        setOrganizationUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch organization users:', error);
      setOrganizationUsers([]);
    }
  };

  /**
   * Checks if there are any validation errors
   * @returns {boolean} True if there are errors, false otherwise
   */
  const hasErrors = () => {
    return Boolean(errors.createdFrom || errors.createdTo);
  };

  /**
   * Handles progression status checkbox changes
   * @param {Object} event - The change event
   * @param {boolean} event.checked - Whether the checkbox is checked
   * @param {string} event.value - The value of the checkbox
   */
  const onProgressionChange = (event) => {
    const { checked, value } = event.target;

    setProgressions((prevProgressions) =>
      checked
        ? [...prevProgressions, +value]
        : prevProgressions.filter((item) => +item !== +value),
    );
  };

  /**
   * Handles representative code checkbox changes
   * @param {Object} event - The change event
   * @param {boolean} event.checked - Whether the checkbox is checked
   * @param {string} event.value - The value of the checkbox
   */
  const onRepCodeChange = (event) => {
    const { checked, value } = event.target;

    setSelectedRepCodes((prevRepCodes) =>
      checked
        ? [...prevRepCodes, value]
        : prevRepCodes.filter((code) => code !== value),
    );
  };

  /**
   * Handles date input changes and validates dates
   * @param {Object} e - The change event
   * @param {string} type - The type of date field ('from' or 'to')
   */
  const onDateChange = (e, type) => {
    const value = e.target.value;
    let newErrors = { ...errors };
    const today = new Date().toISOString().split('T')[0];

    // Check if selected date is in the future
    if (value > today) {
      newErrors[type === 'from' ? 'createdFrom' : 'createdTo'] = "Future dates are not allowed.";
      if (type === "from") {
        setCreatedFrom(value);
      } else {
        setCreatedTo(value);
      }
      setErrors(newErrors);
      return;
    }

    if (type === "from") {
      if (createdTo && value > createdTo) {
        newErrors.createdFrom = "FROM date cannot be later than TO date.";
      } else {
        newErrors.createdFrom = "";
      }
      setCreatedFrom(value);
    } else {
      if (createdFrom && value < createdFrom) {
        newErrors.createdTo = "To date cannot be earlier than FROM date.";
      } else {
        newErrors.createdTo = "";
      }
      setCreatedTo(value);
    }
    setErrors(newErrors);
  };

  /**
   * Applies the selected filters
   */
  const onApplyClick = () => {
    setAppFilterSearchQuery({
      ...appFilterSearchQuery,
      progression_id: progressions,
      created_from: createdFrom,
      created_to: createdTo,
      rep_codes: selectedRepCodes,
      manager_id: selectedManagers,
      uw_ids: selectedUnderwriters,
      organization_id: selectedOrganizations,
      user_id: selectedUser,
      bank: selectedBank,
      left_progression_filter: progress,
      channel: selectedChannel,
    })
    setFilterDrawerOpen(false);
  };

  /**
   * Clears all filters
   */
  const clearFiler = () => {
    setAppFilterSearchQuery({
      left_progression_filter: "",
      search: "",
      sortBy: "id",
      sortValue: "DESC",
      manager_id: [],
      uw_ids: [],
      progression_id: [],
      page: 1,
      user_id: "",
      organization_id: "",
      bank: null,
      created_from: "",
      created_to: "",
      rep_codes: [],
      channel: null,
    });
    setSelectedRepCodes([]);
    setSelectedOrganizations([]);
    setSelectedUser("");
    setOrganizationUsers([]);
    setSelectedBank(null);
    setFilterDrawerOpen(false);
  };

  return (
    <Drawer
      open={filterDrawerOpen}
      direction="right"
      className={style.filter_drawer}
      size={"auto"}
      zIndex={999}
      overlayColor={"#000"}
      overlayOpacity={0.5}
    >
      <div className={style.drawer_container}>
        <div className={style.head}>
          <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M19.2806 18.2194C19.3502 18.289 19.4055 18.3718 19.4432 18.4628C19.4809 18.5539 19.5003 18.6514 19.5003 18.75C19.5003 18.8485 19.4809 18.9461 19.4432 19.0372C19.4055 19.1282 19.3502 19.2109 19.2806 19.2806C19.2109 19.3503 19.1281 19.4056 19.0371 19.4433C18.9461 19.481 18.8485 19.5004 18.7499 19.5004C18.6514 19.5004 18.5538 19.481 18.4628 19.4433C18.3717 19.4056 18.289 19.3503 18.2193 19.2806L11.9999 13.0603L5.78055 19.2806C5.63982 19.4213 5.44895 19.5004 5.24993 19.5004C5.05091 19.5004 4.86003 19.4213 4.7193 19.2806C4.57857 19.1399 4.49951 18.949 4.49951 18.75C4.49951 18.551 4.57857 18.3601 4.7193 18.2194L10.9396 12L4.7193 5.78061C4.57857 5.63988 4.49951 5.44901 4.49951 5.24999C4.49951 5.05097 4.57857 4.8601 4.7193 4.71936C4.86003 4.57863 5.05091 4.49957 5.24993 4.49957C5.44895 4.49957 5.63982 4.57863 5.78055 4.71936L11.9999 10.9397L18.2193 4.71936C18.36 4.57863 18.5509 4.49957 18.7499 4.49957C18.949 4.49957 19.1398 4.57863 19.2806 4.71936C19.4213 4.8601 19.5003 5.05097 19.5003 5.24999C19.5003 5.44901 19.4213 5.63988 19.2806 5.78061L13.0602 12L19.2806 18.2194Z"
                fill="#A4A3A8"
              />
            </svg>
          </ButtonIcon>
        </div>

        <div className={style.header}>
          <h5>Filter</h5>
        </div>

        <div className={style.content}>
          <div className={style.filterContainer} >
            {user?.channel == "partner_channel" && (
              <>
                <TagInput
                  id={'team_lead'}
                  name={'team_lead'}
                  label={'Team Lead'}
                  options={relationshipManagers}
                  value={relationshipManagers.filter((option) => selectedManagers.includes(option.value))}
                  onChange={(value) => {
                    const newManagerIds = value.map((option) => option.value);
                    setSelectedManagers(newManagerIds);
                  }}
                  placeholder={'Select Team Lead'}
                />

                <TagInput
                  id={'underwriter'}
                  name={'underwriter'}
                  label={'Underwriter'}
                  options={relationshipManagers}
                  value={relationshipManagers.filter((option) => selectedUnderwriters.includes(option.value))}
                  onChange={(value) => {
                    const newUnderwriterIds = value.map((option) => option.value);
                    setSelectedUnderwriters(newUnderwriterIds);
                  }}
                  placeholder={'Select Underwriter'}
                />
              </>
            )}

            {isPCAdmin && (
              <>
                <Select
                  id={'channel'}
                  name={'channel'}
                  label={'Channel'}
                  options={[
                    {
                      label: 'Sales Channel',
                      value: 'sales_channel',
                    },
                    {
                      label: 'Partner Channel',
                      value: 'partner_channel',
                    },
                    {
                      label: 'ISV Channel',
                      value: 'isv_channel',
                    }
                  ]}
                  value={selectedChannel}
                  onChange={(value) => {
                    setSelectedChannel(value);
                  }}
                  placeholder={'Select Channel'}
                />

                <Select
                  id={'organization'}
                  name={'organization'}
                  label={'Organization'}
                  options={organizations}
                  value={selectedOrganizations}
                  onChange={(value) => {
                    setSelectedOrganizations(value);
                    fetchOrganizationUsers(value);
                  }}
                  placeholder={'Select Organization'}
                  search={true}
                />

                <Select
                  id={'user'}
                  name={'user'}
                  label={'User'}
                  options={organizationUsers}
                  value={selectedUser}
                  onChange={(value) => {
                    setSelectedUser(value);
                  }}
                  placeholder={'Select User'}
                  search={true}
                />

                <Select
                  id={'bank'}
                  name={'bank'}
                  label={'Bank'}
                  options={banks}
                  value={selectedBank}
                  onChange={(value) => {
                    setSelectedBank(value);
                  }}
                  placeholder={'Select Bank'}
                  search={true}
                />
              </>
            )}

            {user?.channel == "sales_channel" && user?.role == 'admin' && (
              <Select
                id={'user'}
                name={'user'}
                label={'User'}
                options={organizationUsers}
                value={selectedUser}
                onChange={(value) => {
                  setSelectedUser(value);
                }}
                placeholder={'Select User'}
                search={true}
              />
            )}


          </div>

          {/* Partner Channel left Status Filter */}
          {(selectedChannel == 'partner_channel' || selectedChannel == 'isv_channel') && (
            <div className={style.status_filter}>
              <h5 className={style.heading}>STATUS</h5>
              <div className={style.filter_list}>
              {progression_statuses
                .slice(0, showAllStatuses ? progression_statuses.length : 5)
                .map((status) => (
                  <label
                    key={status.progression_id}
                    className={style.filter_list_item}
                    htmlFor={status.progression_id}
                  >
                    <Checkbox
                      name={status.progression_id}
                      id={status.progression_id}
                      value={status.progression_id}
                      onChange={onProgressionChange}
                      checked={progressions.includes(status.progression_id)}
                    />

                    <div>
                      <StatusDot color={`${status.color}`} />
                      <span>{status.name}</span>
                    </div>
                  </label>
                ))}
            </div>
            {progression_statuses.length > 5 && (
              <div className={style.more_wrapper}>
                <button
                  className={style.more_button}
                  onClick={() => setShowAllStatuses(!showAllStatuses)}
                >
                  <div
                    className={style.icon_wrapper}
                    style={{
                      transform: showAllStatuses ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <ArrowDown />
                  </div>
                  <span>{showAllStatuses ? 'Less' : 'More'}</span>
                </button>
              </div>
              )}
            </div>
          )}

          {/* Sales Channel Left Status Filter */}
          {selectedChannel == 'sales_channel' && (
            <div className={style.status_filter}>
              <h5 className={style.heading}>STATUS</h5>
            <div className={style.filter_list}>
              {sales_left_progression_statuses
                .slice(0, showAllSalesStatuses ? sales_left_progression_statuses.length : 5)
                .map((status) => (
                  <label
                    key={status.value}
                    className={style.filter_list_item}
                    htmlFor={status.value}
                  >                   
                    <Radio
                      name={'left_progression_filter'}
                      id={status.value}
                      value={status.value}
                      checked={progress == status.value}
                      onChange={() => {
                        setProgress(status.value);
                      }}
                    />

                    <div
                      onClick={() => {
                        setProgress(status.value);
                      }}
                    >
                      <StatusDot color={`${status.color}`} />
                      <span>{status.name}</span>
                    </div>
                  </label>
                ))}
            </div>
            {sales_left_progression_statuses.length > 5 && (
              <div className={style.more_wrapper}>
                <button
                  className={style.more_button}
                  onClick={() => setShowAllSalesStatuses(!showAllSalesStatuses)}
                >
                  <div
                    className={style.icon_wrapper}
                    style={{
                      transform: showAllSalesStatuses ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <ArrowDown />
                  </div>
                  <span>{showAllSalesStatuses ? 'Less' : 'More'}</span>
                </button>
              </div>
              )}
            </div>
          )}

          <div className={style.date_filter}>
            <h5 className={style.heading}>CREATED</h5>

            <div className={style.date_fields}>
              <div className={style.date_field}>
                <span className={style.icon}>
                  <CalendarIcon />
                </span>
                <input
                  type="date"
                  name="created_from"
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => onDateChange(e, "from")}
                />

              </div>

              <div className={style.date_field}>
                <span className={style.icon}>
                  <CalendarIcon />
                </span>
                <input
                  type="date"
                  name="created_to"
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => onDateChange(e, "to")}
                />
              </div>
            </div>
            {errors.createdFrom && <p className={style.error}>{errors.createdFrom}</p>}
            {errors.createdTo && <p className={style.error}>{errors.createdTo}</p>}
          </div>

          <div className={style.status_filter}>
            <h5 className={style.heading}>REP CODE</h5>
            {isLoadingRepCodes ? (
              <div className={style.loading}>Loading rep codes...</div>
            ) : (
              <div className={style.filterContainer} style={{padding: '8px 24px'}}>
                <TagInput
                  id={'rep_code'}
                  name={'rep_code'}
                  options={repCodes}
                  value={repCodes.filter((option) => selectedRepCodes.includes(option.value))}
                  onChange={(value) => {
                    const newRepCodes = value.map((option) => option.value);
                    setSelectedRepCodes(newRepCodes);
                  }}
                  placeholder={'Select Rep Code'}
                  search={true}
                />
              </div>
            )}
          </div>
        </div>

        <div className={style.footer}>
          <ButtonIcon
            bgColor={"#EDEDF4"}
            textColor={"#050506"}
            onClick={clearFiler}
          >
            Clear
          </ButtonIcon>
          <ButtonIcon
            bgColor={"#4C70EF"}
            textColor={"#FFF"}
            onClick={onApplyClick}
            disabled={hasErrors()}
          >
            Apply
          </ButtonIcon>
        </div>
      </div>
    </Drawer>
  );
}

export default FilterDrawer;
